import { Paper, TextField } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { Segment } from '../../types/segment.type';
import AddressAutocomplete from '../../../shared/components/Forms/AddressAutocomplete.component';
import { ParsedAddress } from '../../../shared/components/Forms/parse-google-address.util';
import { PLACE_SELECTION_HINT, ZIP_CODE_FORMAT_ERROR } from '../../../shared/components/Forms/address-form.constants';

type AddressField = 'title' | 'street' | 'city' | 'zip';

const useStyles = makeStyles({
  paperContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    padding: '1rem 0.75rem',
  },
  cityRow: {
    display: 'flex',
    gap: '1rem',

    '& > div:first-child': {
      width: '35%',
    },
    '& > div:last-child': {
      width: '65%',
    },
  },
});

interface Props {
  updatedSegment: Segment;
  addressLabel: string;
  isPlaceSelected: boolean;
  isZipCodeInvalid: boolean;
  onAddressLabelChange: (value: string) => void;
  onAddressSelected: (place: ParsedAddress) => void;
  onAddressFieldChange: ({ field, value }: { field: AddressField; value: string }) => void;
  onFieldChange: ({ field, value }: { field: 'siren' | 'vatNumber'; value: string }) => void;
}

export function SegmentBillingCard({
  updatedSegment,
  addressLabel,
  isPlaceSelected,
  isZipCodeInvalid,
  onAddressLabelChange,
  onAddressSelected,
  onAddressFieldChange,
  onFieldChange,
}: Props) {
  const classes = useStyles();
  const billingAddress = updatedSegment.billingAddress;
  const isPlacePending = addressLabel.length > 0 && !isPlaceSelected;

  return (
    <section>
      <h4>Facturation</h4>
      <Paper className={classes.paperContent}>
        <TextField
          id="segment-billing-title"
          label="Titre"
          type="text"
          size="small"
          variant="outlined"
          fullWidth
          disabled={!isPlaceSelected}
          value={billingAddress?.title ?? ''}
          onChange={(event) => onAddressFieldChange({ field: 'title', value: event.target.value })}
        />
        <AddressAutocomplete
          id="segment-billing-address-search"
          label="Adresse"
          size="small"
          value={addressLabel}
          onChange={onAddressLabelChange}
          onAddressSelected={onAddressSelected}
          helperText={isPlacePending ? PLACE_SELECTION_HINT : undefined}
        />
        <TextField
          id="segment-billing-street"
          label="Rue"
          type="text"
          size="small"
          variant="outlined"
          fullWidth
          disabled={!isPlaceSelected}
          value={billingAddress?.street ?? ''}
          onChange={(event) => onAddressFieldChange({ field: 'street', value: event.target.value })}
        />
        <div className={classes.cityRow}>
          <TextField
            id="segment-billing-zip"
            label="Code postal"
            type="text"
            size="small"
            variant="outlined"
            disabled={!isPlaceSelected}
            error={isZipCodeInvalid}
            helperText={isZipCodeInvalid ? ZIP_CODE_FORMAT_ERROR : undefined}
            inputProps={{ inputMode: 'numeric', maxLength: 5 }}
            value={billingAddress?.zip ?? ''}
            onChange={(event) => onAddressFieldChange({ field: 'zip', value: event.target.value })}
          />
          <TextField
            id="segment-billing-city"
            label="Ville"
            type="text"
            size="small"
            variant="outlined"
            fullWidth
            disabled={!isPlaceSelected}
            value={billingAddress?.city ?? ''}
            onChange={(event) => onAddressFieldChange({ field: 'city', value: event.target.value })}
          />
        </div>
        <TextField
          id="segment-siren"
          label="SIREN"
          type="text"
          size="small"
          variant="outlined"
          fullWidth
          value={updatedSegment.siren ?? ''}
          onChange={(event) => onFieldChange({ field: 'siren', value: event.target.value })}
        />
        <TextField
          id="segment-vat-number"
          label="N° TVA"
          type="text"
          size="small"
          variant="outlined"
          fullWidth
          value={updatedSegment.vatNumber ?? ''}
          onChange={(event) => onFieldChange({ field: 'vatNumber', value: event.target.value })}
        />
      </Paper>
    </section>
  );
}
